/* eslint-disable */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { onValue, ref as rtdbRef } from "firebase/database";
import CryptoJS from "crypto-js";
import { db } from "../../firebase-config";
import "./OrthancServerData.css";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import io from "socket.io-client";

const extractName = (fullString) => {
  const match = fullString.match(/^[a-zA-Z.\^ ]+/);
  if (!match) return null;
  return match[0].replace(/[^a-zA-Z ]+/g, " ").replace(/\s+/g, " ").trim();
};

const OrthancServerData = () => {
  const [patientsData, setPatientsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [firebasePatientData, setFirebasePatientData] = useState({});
  const [firebaseDataLoaded, setFirebaseDataLoaded] = useState(false);
  const isFetched = useRef(false);

  // Backend monitoring state
  const [backendStatus, setBackendStatus] = useState({
    connected: false,
    running: false,
    currentlyProcessing: null,
    queueLength: 0,
    lastEvent: null
  });
  
  // Trigger for real-time patient list refresh
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const adminName1 = localStorage.getItem("adminName");
  const technicianName = localStorage.getItem("Name");
  const backendServerURL = "http://localhost:3001"; // Backend API
  const orthancServerURL = "http://localhost:8042"; // Orthanc Server
  const secretKey = process.env.REACT_APP_SECRET_KEY;

  // Decrypt data helper
  const decryptData = (encryptedData) => {
    if (!encryptedData || encryptedData === "N/A") return "N/A";
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted || "N/A";
    } catch (error) {
      return "N/A";
    }
  };

  const decryptDataIfNeeded = (data) => {
    if (typeof data === "string" && data.startsWith("U2FsdGVkX1")) {
      return decryptData(data);
    }
    return data;
  };

  const sanitizeFirebaseKey = (key) => {
    return key.replace(/[.#$\/\[\]]/g, '_');
  };

  // Backend monitoring Socket.IO connection
  useEffect(() => {
    const startBackendService = async () => {
      try {
        if (!adminName1) {
          console.error("Admin name not found in localStorage");
          return;
        }
        
        const response = await axios.post("http://localhost:3001/orthanc/start", {
          adminName: adminName1,
          technicianName: technicianName || "Technician"
        });
        
        console.log("✅ Backend service started:", response.data);
      } catch (error) {
        if (error.response?.data?.message === "Orthanc service already running") {
          console.log("ℹ️ Backend service already running");
          setBackendStatus(prev => ({ ...prev, running: true }));
        } else {
          console.error("❌ Failed to start backend service:", error);
        }
      }
    };

    const newBackendSocket = io("http://localhost:3001", {
      transports: ["websocket"],
    });

    newBackendSocket.on("connect", () => {
      setBackendStatus(prev => ({ ...prev, connected: true }));
      startBackendService();
    });

    newBackendSocket.on("disconnect", () => {
      setBackendStatus(prev => ({ ...prev, connected: false }));
    });

    // Backend event listeners
    newBackendSocket.on("serviceStarted", (data) => {
      setBackendStatus(prev => ({ 
        ...prev, 
        running: true,
        lastEvent: { type: 'serviceStarted', data, time: new Date() }
      }));
    });

    newBackendSocket.on("newPatientsDetected", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        lastEvent: { type: 'newPatientsDetected', data, time: new Date() }
      }));
      // Trigger real-time patient list refresh
      setRefreshTrigger(prev => prev + 1);
    });

    newBackendSocket.on("patientQueued", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        lastEvent: { type: 'patientQueued', data, time: new Date() }
      }));
    });

    newBackendSocket.on("uploadStarted", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        currentlyProcessing: data,
        lastEvent: { type: 'uploadStarted', data, time: new Date() }
      }));
    });

    newBackendSocket.on("uploadProgress", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        currentlyProcessing: data,
        lastEvent: { type: 'uploadProgress', data, time: new Date() }
      }));
    });

    newBackendSocket.on("uploadCompleted", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        currentlyProcessing: null,
        lastEvent: { type: 'uploadCompleted', data, time: new Date() }
      }));
      // Trigger real-time patient list refresh
      setRefreshTrigger(prev => prev + 1);
    });

    newBackendSocket.on("uploadError", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        currentlyProcessing: null,
        lastEvent: { type: 'uploadError', data, time: new Date() }
      }));
    });

    newBackendSocket.on("queueStatus", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        queueLength: data.queueLength,
        lastEvent: { type: 'queueStatus', data, time: new Date() }
      }));
    });

    newBackendSocket.on("patientDeleted", (data) => {
      setBackendStatus(prev => ({ 
        ...prev,
        lastEvent: { type: 'patientDeleted', data, time: new Date() }
      }));
      // Trigger real-time patient list refresh
      setRefreshTrigger(prev => prev + 1);
    });
    
    // Listen for immediate patient arrival from /sendName endpoint
    newBackendSocket.on("recall", (data) => {
      // Trigger immediate refresh
      setRefreshTrigger(prev => prev + 1);
    });

    return () => {
      newBackendSocket.disconnect();
    };
  }, [adminName1, technicianName]);

  // Fetch Firebase patient data
  useEffect(() => {
    const folderPathRef = rtdbRef(db, `superadmin/admins/${adminName1}/patients`);
    const folderPathListener = onValue(folderPathRef, (snapshot) => {
      if (snapshot.exists()) {
        setFirebasePatientData(snapshot.val());
        setFirebaseDataLoaded(true);
      } else {
        setFirebaseDataLoaded(true);
      }
    });

    return () => {
      folderPathListener();
    };
  }, [adminName1]);

  // Fetch patients from backend (backend fetches from Orthanc)
  const fetchPatientsInBatches = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendServerURL}/orthanc/patients`);
      
      if (response.data.patients && response.data.patients.length > 0) {
        setPatientsData(response.data.patients);
      } else {
        setPatientsData([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching patients from backend:", error);
      setPatientsData([]);
      setLoading(false);
    }
  };

  // Patient details now fetched from backend, no need for these helper functions

  // Download study
  const handledownloadconfirmation = (studyInstanceUID, patientName) => {
    const confirmed = window.confirm("Are you sure you want to download this study?");
    if (confirmed) {
      downloadStudy(studyInstanceUID, patientName);
    }
  };

  const downloadStudy = async (studyInstanceUID, patientName) => {
    try {
      const link = document.createElement("a");
      link.href = `${orthancServerURL}/orthanc/studies/${studyInstanceUID}/archive`;
      link.download = `${patientName}.zip`;
      link.click();
    } catch (error) {
      console.error("Error downloading study:", error);
    }
  };

  // Check if patient is uploaded to Firebase
  const isPatientUploaded = (patient) => {
    const sanitizedPatientName = extractName(patient.MainDicomTags.PatientName);
    const incomingPatientID = patient.MainDicomTags.PatientID;
    
    if (firebasePatientData) {
      for (const [key, patientData] of Object.entries(firebasePatientData)) {
        if (key.startsWith(`Patient-${sanitizeFirebaseKey(sanitizedPatientName)}`)) {
          const encryptedStoredID = patientData.ID || patientData.PatientID;
          const storedID = decryptDataIfNeeded(encryptedStoredID);
          
          if (storedID === incomingPatientID) {
            return true;
          }
        }
      }
    }
    return false;
  };

  // Search and pagination
  const filterPatientsBySearchQuery = (query) => {
    if (!query) return patientsData;
    
    const lowerQuery = query.toLowerCase();
    return patientsData.filter((patient) => {
      const patientName = patient.PatientName?.toLowerCase() || "";
      const patientID = patient.MainDicomTags.PatientID?.toLowerCase() || "";
      return patientName.includes(lowerQuery) || patientID.includes(lowerQuery);
    });
  };

  const getCurrentPagePatients = () => {
    const filteredPatients = filterPatientsBySearchQuery(searchQuery);
    const startIndex = (currentPage - 1) * 10;
    const endIndex = startIndex + 10;
    return filteredPatients.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filteredPatients = filterPatientsBySearchQuery(searchQuery);
    return Math.ceil(filteredPatients.length / 10);
  };

  // Initial fetch when backend connects - with retry for backend warmup
  useEffect(() => {
    if (backendStatus.connected && patientsData.length === 0) {
      // Retry mechanism to wait for backend to poll Orthanc
      const fetchWithRetry = async (retries = 3) => {
        try {
          const response = await axios.get(`${backendServerURL}/orthanc/patients`);
          const patientCount = response.data.patients?.length || 0;
          
          if (patientCount === 0 && retries > 0) {
            setTimeout(() => fetchWithRetry(retries - 1), 2000);
          } else {
            // Update the patients data
            if (patientCount > 0) {
              setPatientsData(response.data.patients);
            } else {
              setPatientsData([]);
            }
            setLoading(false);
          }
        } catch (error) {
          console.error("Error fetching patients:", error);
          setLoading(false);
        }
      };
      
      fetchWithRetry();
    }
  }, [backendStatus.connected]);
  
  // Real-time refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchPatientsInBatches();
    }
  }, [refreshTrigger]);

  // Auto-adjust pagination
  useEffect(() => {
    const filteredPatients = filterPatientsBySearchQuery(searchQuery);
    const totalPages = Math.ceil(filteredPatients.length / 10);
    
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (filteredPatients.length === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [patientsData.length, currentPage, searchQuery]);

  const currentPagePatients = getCurrentPagePatients();
  const totalPages = getTotalPages();

  return (
    <div className="container-fluid">
      <h2 className="text-center adminlist">Server Patient List</h2>

      {/* Back button */}
      <Link to="/Technician" className="homeButton">
        <span id="backText" className="backText">Back</span>
        <i id="backIcon" className="fas fa-long-arrow-alt-left fa-lg fa-2x"></i>
      </Link>

      {/* Search bar */}
      <div className="DivsearchBarTechPanelOSD">
        <div className="search-bar searchBarTechPanelOSD">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Backend Monitoring Status */}
      <div className="backend-monitor">
        <div className="backend-monitor-header">
          <h3>🖥️ Backend Service Status</h3>
          <div className={`status-indicator ${backendStatus.connected ? 'connected' : 'disconnected'}`}>
            {backendStatus.connected ? '🟢 Connected' : '🔴 Disconnected'}
          </div>
        </div>

        {backendStatus.connected && (
          <div className="backend-monitor-content">
            {backendStatus.currentlyProcessing && (
              <div className="current-upload">
                <div className="upload-header">
                  <span className="upload-icon">⬆️</span>
                  <span className="upload-text">Currently Uploading</span>
                </div>
                <div className="upload-details">
                  <p className="patient-name">{backendStatus.currentlyProcessing.patientName}</p>
                  <div className="progress-info">
                    Study {backendStatus.currentlyProcessing.currentStudy || 0} of {backendStatus.currentlyProcessing.totalStudies}
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: `${((backendStatus.currentlyProcessing.currentStudy || 0) / backendStatus.currentlyProcessing.totalStudies) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {backendStatus.queueLength > 0 && (
              <div className="queue-status">
                <span className="queue-icon">📋</span>
                <span className="queue-text">
                  {backendStatus.queueLength} patient(s) in queue
                </span>
              </div>
            )}

            {backendStatus.lastEvent && (
              <div className="last-event">
                <div className="event-header">Latest Activity:</div>
                <div className="event-content">
                  <span className="event-icon">
                    {backendStatus.lastEvent.type === 'serviceStarted' ? '🚀' :
                     backendStatus.lastEvent.type === 'newPatientsDetected' ? '🔍' :
                     backendStatus.lastEvent.type === 'patientQueued' ? '📋' :
                     backendStatus.lastEvent.type === 'uploadStarted' ? '⬆️' :
                     backendStatus.lastEvent.type === 'uploadProgress' ? '📊' :
                     backendStatus.lastEvent.type === 'uploadCompleted' ? '✅' :
                     backendStatus.lastEvent.type === 'uploadError' ? '❌' :
                     backendStatus.lastEvent.type === 'queueStatus' ? '📊' :
                     backendStatus.lastEvent.type === 'patientDeleted' ? '🗑️' : '📡'}
                  </span>
                  <span className="event-message">
                    {backendStatus.lastEvent.type === 'serviceStarted' ? `Service started for ${backendStatus.lastEvent.data.adminName}` :
                     backendStatus.lastEvent.type === 'newPatientsDetected' ? `${backendStatus.lastEvent.data.count} new patient(s) detected` :
                     backendStatus.lastEvent.type === 'patientQueued' ? `${backendStatus.lastEvent.data.patientName} added to queue` :
                     backendStatus.lastEvent.type === 'uploadStarted' ? `Uploading ${backendStatus.lastEvent.data.patientName}...` :
                     backendStatus.lastEvent.type === 'uploadProgress' ? `Uploading ${backendStatus.lastEvent.data.patientName} (Study ${backendStatus.lastEvent.data.currentStudy}/${backendStatus.lastEvent.data.totalStudies})` :
                     backendStatus.lastEvent.type === 'uploadCompleted' ? `${backendStatus.lastEvent.data.patientName} uploaded successfully` :
                     backendStatus.lastEvent.type === 'uploadError' ? `Error uploading ${backendStatus.lastEvent.data.patientName}` :
                     backendStatus.lastEvent.type === 'queueStatus' ? `Queue: ${backendStatus.lastEvent.data.queueLength} patient(s) waiting` :
                     backendStatus.lastEvent.type === 'patientDeleted' ? `Patient deleted (${backendStatus.lastEvent.data.reason})` :
                     'Activity detected'}
                  </span>
                </div>
                <div className="event-time">
                  {backendStatus.lastEvent.time.toLocaleTimeString()}
                </div>
              </div>
            )}

            {!backendStatus.currentlyProcessing && backendStatus.queueLength === 0 && (
              <div className="idle-state">
                <span className="idle-icon">💤</span>
                <p>Monitoring Orthanc for new patients...</p>
              </div>
            )}
          </div>
        )}

        {!backendStatus.connected && (
          <div className="backend-monitor-content">
            <div className="disconnected-message">
              <span className="warning-icon">⚠️</span>
              <p>Backend service not connected</p>
              <p className="help-text">Make sure the backend server is running</p>
            </div>
          </div>
        )}
      </div>

      {/* Patient list */}
      {loading ? (
        <div className="spinner-overlay">
          <div className="spinner"></div>
          <p>Loading patient data...</p>
        </div>
      ) : (
        <div style={{ marginTop: "100px" }}>
          <table className="table table-bordered table-striped patient-table">
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Patient Name</th>
                <th>Patient ID</th>
                <th>Studies</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPagePatients.map((patient, index) => {
                const isUploaded = isPatientUploaded(patient);
                return (
                  <tr key={patient.ID}>
                    <td>{(currentPage - 1) * 10 + index + 1}</td>
                    <td>{patient.PatientName || "N/A"}</td>
                    <td>{patient.MainDicomTags.PatientID || "N/A"}</td>
                    <td>{patient.Studies?.length || 0}</td>
                    <td>
                      {isUploaded ? (
                        <span style={{ color: "green" }}>✔️ Uploaded</span>
                      ) : (
                        <span style={{ color: "orange" }}>⏳ Pending</span>
                      )}
                    </td>
                    <td>
                      {patient.Studies && patient.Studies.length > 0 && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            handledownloadconfirmation(
                              patient.Studies[0].ID,
                              patient.PatientName
                            )
                          }
                        >
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrthancServerData;
